import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";

import { getIos, getCampaigns, getAuditLog, ApiError } from "../api.js";
import { STATUS_MAP } from "./StatusBadge.jsx";
import { loadUploadedIos } from "../lib/localIos.js";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatCurrency(n) {
  if (n == null || n === "") return "$0";
  const num = typeof n === "number" ? n : Number(n);
  if (Number.isNaN(num)) return "$0";
  if (num >= 1_000_000) return `$${(num / 1_000_000).toFixed(1)}M`;
  if (num >= 1_000) return `$${(num / 1_000).toFixed(0)}K`;
  return `$${num.toFixed(0)}`;
}

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const diff = Date.now() - d.getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function OverviewPage() {
  const navigate = useNavigate();

  const [ios, setIos] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [auditEntries, setAuditEntries] = useState([]);
  const [localIos, setLocalIos] = useState(loadUploadedIos);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [iosRes, campRes, auditRes] = await Promise.all([
        getIos({ limit: 200 }),
        getCampaigns({ limit: 100 }).catch(() => ({ data: [] })),
        getAuditLog({ limit: 20 }).catch(() => ({ data: [] })),
      ]);
      setIos(iosRes.data || iosRes || []);
      setCampaigns(campRes.data || []);
      setAuditEntries(auditRes.data || []);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  useEffect(() => {
    const onStorage = () => setLocalIos(loadUploadedIos());
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // ---- Derived stats ----
  const allIos = [...localIos, ...ios];
  const stats = {
    total: allIos.length,
    draft: 0,
    pending: 0,
    approved: 0,
    rejected: 0,
    totalBudget: 0,
    pendingReview: 0,
  };
  for (const io of allIos) {
    const st = io.status || "draft";
    if (stats[st] != null) stats[st]++;
    if (st === "draft" || st === "pending") stats.pendingReview++;
    const budget = Number(io.total_gross_budget);
    if (!Number.isNaN(budget)) stats.totalBudget += budget;
  }
  const campaignStats = {
    total: campaigns.length,
    pending: campaigns.filter((c) => c.status === "pending" || c.status === "generating").length,
    complete: campaigns.filter((c) => c.status === "complete" || c.status === "live").length,
    failed: campaigns.filter((c) => c.status === "failed").length,
  };

  // Status breakdown for the bar
  const statusBreakdown = [
    { key: "approved", label: "Approved", count: stats.approved, className: "bar-approved" },
    { key: "pending", label: "Pending", count: stats.pending, className: "bar-pending" },
    { key: "draft", label: "Draft", count: stats.draft, className: "bar-draft" },
    { key: "rejected", label: "Rejected", count: stats.rejected, className: "bar-rejected" },
  ].filter((s) => s.count > 0);

  const totalForBar = statusBreakdown.reduce((sum, s) => sum + s.count, 0) || 1;

  return (
    <div className="overview-page container">
      <div className="overview-header">
        <h1>Overview</h1>
        <div className="overview-header-actions">
          <button type="button" className="btn" onClick={fetchAll} disabled={loading}>
            {loading ? "Refreshing..." : "Refresh"}
          </button>
          <button type="button" className="btn btn-primary" onClick={() => navigate("/upload")}>
            + New IO
          </button>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {/* KPI Cards */}
      <div className="kpi-grid">
        <div className="kpi-card" onClick={() => navigate("/dashboard")}>
          <div className="kpi-value">{stats.total}</div>
          <div className="kpi-label">Total IOs</div>
          {localIos.length > 0 && (
            <div className="kpi-sub">{localIos.length} local (not submitted)</div>
          )}
        </div>
        <div className="kpi-card kpi-pending" onClick={() => navigate("/dashboard?status=pending")}>
          <div className="kpi-value">{stats.pendingReview}</div>
          <div className="kpi-label">Pending Review</div>
        </div>
        <div className="kpi-card" onClick={() => navigate("/dashboard?status=approved")}>
          <div className="kpi-value">{stats.approved}</div>
          <div className="kpi-label">Approved</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-value">{formatCurrency(stats.totalBudget)}</div>
          <div className="kpi-label">Total Budget</div>
        </div>
        <div className="kpi-card" onClick={() => navigate("/campaigns")}>
          <div className="kpi-value">{campaignStats.total}</div>
          <div className="kpi-label">Campaigns</div>
          {campaignStats.complete > 0 && (
            <div className="kpi-sub">{campaignStats.complete} complete</div>
          )}
        </div>
      </div>

      {/* Status Breakdown Bar */}
      <div className="overview-section">
        <h3 className="section-title">IO Status Breakdown</h3>
        <div className="status-bar">
          {statusBreakdown.map((s) => (
            <div
              key={s.key}
              className={`status-bar-segment ${s.className}`}
              style={{ flexBasis: `${(s.count / totalForBar) * 100}%` }}
              title={`${s.label}: ${s.count}`}
            >
              <span className="status-bar-label">{s.label}</span>
              <span className="status-bar-count">{s.count}</span>
            </div>
          ))}
          {statusBreakdown.length === 0 && (
            <div className="status-bar-empty">No IOs yet</div>
          )}
        </div>
      </div>

      {/* Recent IOs + Recent Activity */}
      <div className="overview-grid">
        <div className="overview-panel">
          <div className="overview-panel-header">
            <h3 className="section-title">Recent IOs</h3>
            <Link to="/dashboard" className="overview-panel-link">View all</Link>
          </div>
          {allIos.length === 0 ? (
            <div className="overview-empty">No IOs ingested yet. Upload one to get started.</div>
          ) : (
            <table className="overview-table">
              <thead>
                <tr>
                  <th>IO #</th>
                  <th>Advertiser</th>
                  <th>Status</th>
                  <th className="num">Budget</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {allIos.slice(0, 8).map((io) => {
                  const meta = STATUS_MAP[io.status] || STATUS_MAP.draft;
                  return (
                    <tr key={io.id} className="overview-row" onClick={() => navigate(`/ios/${encodeURIComponent(io.id)}`)}>
                      <td className="mono">{io.io_number || io.id}</td>
                      <td>{io.advertiser_name || "—"}</td>
                      <td>
                        <span className={`status-badge ${meta.className}`}>{meta.label}</span>
                      </td>
                      <td className="num">{formatCurrency(io.total_gross_budget)}</td>
                      <td className="muted">{timeAgo(io.createdAt || io.created_at)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="overview-panel">
          <div className="overview-panel-header">
            <h3 className="section-title">Recent Activity</h3>
            <Link to="/audit-log" className="overview-panel-link">View all</Link>
          </div>
          {auditEntries.length === 0 ? (
            <div className="overview-empty">No activity logged yet.</div>
          ) : (
            <ul className="activity-list">
              {auditEntries.slice(0, 12).map((entry, i) => (
                <li key={entry.id || i} className="activity-item">
                  <span className="activity-action">{entry.action || "—"}</span>
                  <span className="activity-summary">{entry.summary || ""}</span>
                  <span className="activity-time muted">{timeAgo(entry.timestamp || entry.createdAt)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Campaign Status Summary */}
      {campaignStats.total > 0 && (
        <div className="overview-section">
          <h3 className="section-title">Campaign Generation Status</h3>
          <div className="campaign-mini-grid">
            <div className="mini-stat">
              <span className="mini-stat-value">{campaignStats.pending}</span>
              <span className="mini-stat-label">In Progress</span>
            </div>
            <div className="mini-stat">
              <span className="mini-stat-value">{campaignStats.complete}</span>
              <span className="mini-stat-label">Complete</span>
            </div>
            <div className="mini-stat">
              <span className="mini-stat-value">{campaignStats.failed}</span>
              <span className="mini-stat-label">Failed</span>
            </div>
            <div className="mini-stat">
              <span className="mini-stat-value">{campaignStats.total}</span>
              <span className="mini-stat-label">Total</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}